import React, { useState, useEffect } from 'react';
import UISectionWrapper, {
  UISectionHeader,
  UISectionBody,
} from '@/components/shared/UISectionWrapper';
import UISectionTitle from '@/components/shared/UISectionTitle';
import UIIconExtraButton from '@/components/shared/UIIconExtraButton';
import DebtorsTable from '../DebtorsTable';
import './ListOfDebtorsSection.scss';
import UIPaginator from '@/components/shared/UIPaginator';
import { usePayments } from '@/hooks/usePayments';

export interface TenantData {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  debt: number;
  currency: string;
  property: string;
  unit: string;
}

interface ListOfDebtorsSectionProps {
  title?: string;
  id?: string,
  data?: TenantData[];
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  onTenantAction?: (tenantId: string, action: string) => void;
}

const baseClass = 'ListOfDebtorsSection';

const mockHandlers = {
  onToggleCollapse: () => console.log('Toggle tenants list collapse'),
  onTenantAction: (tenantId: string, action: string) => console.log('Tenant action:', tenantId, action)
};

const ListOfDebtorsSection: React.FC<ListOfDebtorsSectionProps> = ({
                                                                     id,
                                                                     title = 'List of debtors',
                                                                     data: propData,
                                                                     isCollapsed: initialCollapsed = false,
                                                                     onToggleCollapse,
                                                                     onTenantAction = mockHandlers.onTenantAction
                                                                   }) => {
  const [isCollapsed, setIsCollapsed] = useState(initialCollapsed);
  const [debtorsData, setDebtorsData] = useState<TenantData[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalDebt, setTotalDebt] = useState(0);
  const { getDebtors } = usePayments();

  // Стан для пагінації
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);

  const adaptDebtorData = (debtors: any[]): TenantData[] => {
    let calculatedTotalDebt = 0;

    const adaptedData = debtors.map((debtor: any) => {
      let tenantName = '';
      if (debtor.tenant?.name) {
        tenantName = debtor.tenant.name;
      } else if (debtor.tenant?.firstname && debtor.tenant?.lastname) {
        tenantName = `${debtor.tenant.firstname} ${debtor.tenant.lastname}`;
      } else {
        tenantName = 'Unknown User';
      }

      // Підраховуємо загальну суму боргу з overduePayments
      const debtAmount = debtor.overduePayments?.reduce((total: number, payment: any) => {
        return total + parseFloat(payment.amount || '0');
      }, 0) || 0;

      calculatedTotalDebt += debtAmount;

      // Беремо валюту з першого платежу або USD за замовчуванням
      const currency = debtor.overduePayments?.[0]?.currency || 'USD';

      return {
        id: debtor.tenant?.id?.toString() || '',
        name: tenantName.trim(),
        email: debtor.tenant?.email || '',
        avatar: debtor.tenant?.avatar || undefined,
        debt: debtAmount,
        currency: currency === 'USD' ? '$' : currency,
        property: debtor.property?.name || 'N/A',
        unit: debtor.unit?.number || 'N/A',
      };
    });

    setTotalDebt(calculatedTotalDebt);
    return adaptedData;
  };

  const fetchDebtors = async () => {
    try {
      setLoading(true);
      const response = await getDebtors();
      if (response) {
        const adaptedData = adaptDebtorData(response);
        setDebtorsData(adaptedData);
      } else {
        setDebtorsData([]);
        setTotalDebt(0);
      }
    } catch (error) {
      console.error('Error fetching debtors:', error);
      setDebtorsData([]);
      setTotalDebt(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (propData) {
      setDebtorsData(propData);
      setLoading(false);
      // Підраховуємо загальний борг для переданих даних
      const total = propData.reduce((sum, debtor) => sum + debtor.debt, 0);
      setTotalDebt(total);
    } else {
      fetchDebtors();
    }
  }, [propData]);

  const handleToggleCollapse = () => {
    setIsCollapsed(prev => !prev);
    onToggleCollapse?.();
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    console.log('Page changed to:', page);
  };

  // Використовуємо реальні дані або пропси
  const dataToUse = propData || debtorsData;

  // Розрахунок даних для поточної сторінки
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPageData = dataToUse.slice(startIndex, endIndex);
  const totalPages = Math.ceil(dataToUse.length / itemsPerPage);

  const formatCurrency = (amount: number, currency: string = '$') => {
    return `${currency}${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const headerAction = (
    <UIIconExtraButton
      icon={isCollapsed ? 'expand' : 'collapse'}
      onClick={handleToggleCollapse}
      ariaLabel={isCollapsed ? 'Expand list' : 'Collapse list'}
    />
  );

  const titleWithDebt = dataToUse.length > 0
    ? `${title} (${formatCurrency(totalDebt)})`
    : title;

  return (
    <UISectionWrapper
      className={baseClass}
      align="stretch"
      id={id}
    >
      <UISectionHeader>
        <UISectionTitle
          title={titleWithDebt}
          action={headerAction}
          showDivider={true}
        />
      </UISectionHeader>

      {!isCollapsed && (
        <UISectionBody>
          <div className={`${baseClass}__content`}>
            {loading ? (
              <div className={`${baseClass}__loading`}>
                Loading debtors...
              </div>
            ) : dataToUse.length === 0 ? (
              <div className={`${baseClass}__empty`}>
                No debtors found
              </div>
            ) : (
              <>
                <DebtorsTable
                  data={currentPageData}
                  onTenantAction={onTenantAction}
                />

                {totalPages > 1 && (
                  <UIPaginator
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    className={`${baseClass}__paginator`}
                  />
                )}
              </>
            )}
          </div>
        </UISectionBody>
      )}
    </UISectionWrapper>
  );
};

export default ListOfDebtorsSection;